/** The consent question itself: who asks, as whom, for what, where it returns, and for how long. */

import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import type { McpConsent } from '@/api/types';
import { AuthCard } from '@/routes/SignInLocal';
import { ScopeChecks } from '@/routes/account/ScopeChecks';
import { SCOPES } from '@/routes/account/mcpScopes';
import { useSession } from '@/session/SessionContext';
import { ActionButton } from '@/ui/ActionButton';

/** Any program on the computer can listen on a loopback port, so the address alone proves nothing about who asked. */
export const LOOPBACK_WARNING =
  'This returns to a program on this computer. Allow only if you just started the sign-in from Claude Code or another tool on it.';

function Answers({ busy, none, onAnswer }: { busy: boolean | null; none: boolean; onAnswer: (allow: boolean) => void }) {
  const answering = busy !== null;
  return (
    <div className="form-actions">
      <ActionButton
        type="button"
        className="btn btn-secondary"
        state={{ pending: busy === false, error: null }}
        disabled={answering}
        label="Deny"
        pendingLabel="Denying…"
        onClick={() => onAnswer(false)}
      />
      <ActionButton
        type="button"
        state={{ pending: busy === true, error: null }}
        disabled={answering || none}
        label="Allow"
        pendingLabel="Allowing…"
        onClick={() => onAnswer(true)}
      />
    </div>
  );
}

export function ConsentCard({
  consent,
  busy,
  onAnswer,
}: {
  consent: McpConsent;
  /** The answer on its way (true Allow, false Deny), or null while the person decides. */
  busy: boolean | null;
  /** Allow carries the permissions the person ticked; Deny carries none. */
  onAnswer: (allow: boolean, scopes: string[]) => void;
}) {
  const { user } = useSession();
  const held = SCOPES.filter((scope) => consent.scopes.includes(scope.key));
  // Everything the person holds starts ticked, as before the choice existed: unticking is the deliberate act.
  const [chosen, setChosen] = useState<string[]>(consent.defaults);
  const toggle = (key: string, on: boolean) => setChosen((now) => (on ? [...now, key] : now.filter((k) => k !== key)));
  return (
    <AuthCard>
      <div className="stack-md">
        <h1 className="title-xl">
          Allow <strong>{consent.tool_name}</strong> to use Wynxx Discover as <strong>{user?.display_name}</strong>?
        </h1>
        {consent.client_name !== consent.tool_name && <p className="hint">Registered as {consent.client_name}</p>}
        <ScopeChecks held={held} chosen={chosen} onToggle={toggle} />
        <p>
          It sends you back to <code className="mono wrap-anywhere">{consent.redirect_uri}</code>
        </p>
        {consent.loopback && (
          <div className="alert alert-warning">
            <AlertTriangle aria-hidden="true" />
            <div className="a-body">{LOOPBACK_WARNING}</div>
          </div>
        )}
        <p className="hint">The connection lasts up to {consent.max_days} days. You can end it any time in Account → MCP.</p>
        <Answers busy={busy} none={chosen.length === 0} onAnswer={(allow) => onAnswer(allow, allow ? chosen : [])} />
      </div>
    </AuthCard>
  );
}
